// Zustand store for multiplayer gameplay. Server-mirror discipline —
// no optimistic updates. Mirrors soloStore.ts in shape; key differences:
//   - phase machine adds 'lobby' (pre-start) and 'spectator' (after the
//     local player is eliminated).
//   - subscribes to socket events (round_start, round_battle_resolved,
//     etc.) so other players' actions push state in real time.
//   - holds spectatorRounds[] so an eliminated player can rewatch any
//     prior round's battles.

import { create } from 'zustand'
import type {
  HeroIdentity,
  LobbyStatePublic,
  MatchOverPayload,
  MultiPairingResolved,
  MultiSessionState,
  RoundTimerSeconds,
  SpectatorRoundPayload,
  UnitDef,
} from '@origami/shared'
import {
  TRANSIENT_SHOP_ERROR_CODES,
  friendlyShopErrorMessage,
} from '@origami/shared'
import {
  createLobby,
  joinLobby,
  leaveLobby,
  startMatch,
} from './lobbyApi'
import {
  abandonMultiSession,
  buyFromMultiShop,
  combineMultiUnits,
  getMultiSession,
  kickMultiParticipant,
  readyForMultiRound,
  reorderMultiUnits,
  rerollMultiShop,
  sellFromMultiShop,
  toggleMultiLock,
} from './multiApi'
import {
  disconnectMultiSocket,
  isMultiSocketLive,
  subscribeMultiSocket,
  type MultiSocketHandlers,
} from './socket'
import { type BuffFloater, diffBoardForFloaters } from '../game/buffFloaters'
import { playSfx } from '../sound'

type Phase =
  | 'idle'
  | 'loading'
  | 'hub'         // pre-lobby (host or join chooser)
  | 'lobby'       // pending match
  | 'shop'        // active match, shop phase
  | 'battle'      // playing local pairing replay
  | 'between'     // brief inter-round splash
  | 'spectator'   // local player eliminated; watching others
  | 'finished'    // match over
  | 'error'

interface MultiState {
  session: MultiSessionState | null
  pendingBattle: MultiPairingResolved | null
  /** A round_start payload that arrived while we were still watching
   *  the previous round's battle replay. Held back so the player isn't
   *  yanked out of their replay; consumed by dismissBattle. Server
   *  also enforces a 30s replay-viewing window before emitting
   *  round_start, but this guard makes the client robust if the
   *  player closes the replay early or the timer slips. */
  pendingNextRoundState: MultiSessionState | null
  matchOver: MatchOverPayload | null
  spectatorRounds: SpectatorRoundPayload[]
  /** Pairing the spectator is currently watching (null = picker). */
  watchingPairingId: string | null
  phase: Phase
  /** Session-fatal error. See soloStore for the split-vs-notice
   *  rationale; recoverable shop-action failures land in `notice`. */
  error: string | null
  /** Transient action-failure toast (e.g. "Not enough gold..."). */
  notice: { code: string; message: string; at: number } | null
  unitsById: Record<string, UnitDef>
  /** Returned by `subscribeMultiSocket` — call to detach. Set on the
   *  first action that needs the socket; cleared on logout/leave. */
  socketCleanup: (() => void) | null
  /** Floating HP/ATK deltas queued by `applySession` when the local
   *  player's shop board climbs in stats. Mirrors solo's queue. */
  buffFloaters: BuffFloater[]
  clearBuffFloater: (id: string) => void

  loadCatalog: () => Promise<void>
  ensureSocket: () => void
  resume: () => Promise<void>
  hostLobby: (
    body: { round_timer_seconds?: RoundTimerSeconds; identity: HeroIdentity },
  ) => Promise<void>
  joinByCode: (code: string, identity: HeroIdentity) => Promise<void>
  leave: () => Promise<void>
  start: () => Promise<void>

  buy: (slot_index: number) => Promise<void>
  buyToSlot: (
    slot_index: number,
    target: { where: 'active' | 'bench'; position: number },
    opts?: { floaterDelayMs?: number },
  ) => Promise<void>
  sell: (where: 'active' | 'bench', position: number) => Promise<void>
  reroll: () => Promise<void>
  toggleLock: (slot_index: number) => Promise<void>
  reorder: (
    from: { where: 'active' | 'bench'; position: number },
    to: { where: 'active' | 'bench'; position: number },
  ) => Promise<void>
  combine: (
    from: { where: 'active' | 'bench'; position: number },
    to: { where: 'active' | 'bench'; position: number },
    opts?: { floaterDelayMs?: number },
  ) => Promise<void>
  ready: () => Promise<void>
  /** Host-only: mark a stalled / disconnected participant ready so
   *  the round can resolve. POSTs /api/multi/kick; the server
   *  broadcasts the resulting participant_state_update over the
   *  socket so all roster chips update in lockstep. */
  kick: (participant_id: string) => Promise<void>
  /** Drop out of the current multi session entirely (lobby OR active
   *  match). Server-side: marks participant eliminated for active
   *  matches, deletes for pending. Client-side: tears down socket and
   *  clears local store so the next /?multi entry shows the hub. */
  abandon: () => Promise<void>
  /** Dismiss the current transient notice toast. */
  clearNotice: () => void

  dismissBattle: () => void
  watchSpectatorPairing: (pairing_id: string) => void
  closeSpectatorReplay: () => void
  exitMatch: () => void
}

function lobbyPhase(lobby: LobbyStatePublic | null, isEliminated: boolean): Phase {
  if (!lobby) return 'idle'
  if (lobby.status === 'pending') return 'lobby'
  if (lobby.status === 'finished' || lobby.status === 'abandoned') return 'finished'
  if (isEliminated) return 'spectator'
  return 'shop'
}

export const useMultiStore = create<MultiState>((set, get) => {
  function applySession(
    state: MultiSessionState,
    opts?: { floaterDelayMs?: number },
  ) {
    const computed = lobbyPhase(state.lobby, state.is_eliminated)
    // Preserve an in-progress battle / spectator replay. The
    // /api/multi/ready HTTP response races the socket's
    // round_battle_resolved push: when the local player is the
    // SECOND to ready (so resolveRound runs inline inside their
    // /ready handler), the socket event lands first + flips phase
    // to 'battle', then the HTTP response would otherwise clobber
    // them back to 'shop' here — leaving them stuck on the shop
    // until the next round_start arrives ~30s later.
    // onParticipantStateUpdate has the same guard for the same
    // reason; mirror it. 'spectator' included for the eliminated-
    // player flow where the spectator picker has already engaged.
    const cur = get().phase
    const phase = (cur === 'battle' || cur === 'spectator') ? cur : computed
    // Diff prev → new shop to queue HP/ATK floaters (sage_owl,
    // walking_stick, on_purchase, on_combine_witnessed). Mirrors
    // solo's applyState. First-load no-op when prev?.shop is null.
    const prev = get().session
    const newFloaters = prev?.shop
      ? diffBoardForFloaters(prev.shop, state.shop, { delayMs: opts?.floaterDelayMs })
      : []
    const existingFloaters = get().buffFloaters
    // Clear both hard error AND transient notice on success.
    set({
      session: state,
      phase,
      error: null,
      notice: null,
      buffFloaters: newFloaters.length > 0
        ? [...existingFloaters, ...newFloaters]
        : existingFloaters,
    })
  }
  function applyError(code: string) {
    // Mirror soloStore: recoverable shop-action failures become a
    // toast notice so the player stays in shop/lobby instead of
    // flipping to an error phase.
    if (TRANSIENT_SHOP_ERROR_CODES.has(code)) {
      set({
        notice: { code, message: friendlyShopErrorMessage(code), at: Date.now() },
      })
      return
    }
    set({ error: code, phase: 'error' })
  }

  const handlers: MultiSocketHandlers = {
    onLobbyUpdate(lobby) {
      const cur = get().session
      if (!cur) return
      if (lobby.match_id !== cur.match_id) return
      // Friendly door-knock when a new player joins the pending lobby.
      if (
        lobby.status === 'pending' &&
        lobby.participants.length > cur.lobby.participants.length
      ) {
        playSfx('lobby-join')
      }
      const next: MultiSessionState = { ...cur, lobby }
      set({ session: next })
    },
    onMatchStart(lobby) {
      const cur = get().session
      if (!cur) return
      if (lobby.match_id !== cur.match_id) return
      set({ session: { ...cur, lobby }, phase: 'shop', error: null })
    },
    onRoundStart(state) {
      // Server-pushed full session state for this player at the start of
      // a new round. If the local player is still watching their last
      // pairing's replay, stash the new state and let dismissBattle
      // apply it on close — yanking them out of the replay mid-frame
      // is the original "battle resolved instantly" bug.
      if (get().phase === 'battle' || get().pendingBattle) {
        set({ pendingNextRoundState: state })
        return
      }
      applySession(state)
      set({ pendingBattle: null, pendingNextRoundState: null })
    },
    onParticipantStateUpdate(lobby) {
      const cur = get().session
      if (!cur) return
      if (lobby.match_id !== cur.match_id) return
      // Find ourselves in the new roster via the participant_id that
      // ships on the session (stable across the match). Use that row's
      // is_eliminated as the authoritative local elimination flag.
      const me = lobby.participants.find(
        (p) => p.participant_id === cur.participant_id,
      )
      const eliminated = me?.is_eliminated ?? cur.is_eliminated
      const phase = lobbyPhase(lobby, eliminated)
      set({
        session: { ...cur, lobby, is_eliminated: eliminated },
        // Don't override an in-progress 'battle' replay — the next
        // dismissBattle / closeSpectatorReplay will reconverge phase.
        phase: get().phase === 'battle' ? 'battle' : phase,
      })
    },
    onRoundBattleResolved(payload) {
      set({ pendingBattle: payload, phase: 'battle' })
    },
    onSpectatorRound(payload) {
      set((s) => ({
        spectatorRounds: [...s.spectatorRounds, payload],
        // If we just got eliminated this round, the participant_state_update
        // already flipped phase to 'spectator'. Don't override an in-progress
        // 'battle' replay — the spectator picker waits.
      }))
    },
    onMatchOver(payload) {
      set({ matchOver: payload, phase: 'finished' })
    },
  }

  return {
    session: null,
    pendingBattle: null,
    pendingNextRoundState: null,
    matchOver: null,
    spectatorRounds: [],
    watchingPairingId: null,
    phase: 'idle',
    error: null,
    notice: null,
    unitsById: {},
    socketCleanup: null,
    buffFloaters: [],

    clearBuffFloater: (id) => {
      set({ buffFloaters: get().buffFloaters.filter((f) => f.id !== id) })
    },

    async loadCatalog() {
      if (Object.keys(get().unitsById).length > 0) return
      try {
        const res = await fetch('/api/units', { credentials: 'include' })
        if (!res.ok) return
        const units = (await res.json()) as UnitDef[]
        const byId: Record<string, UnitDef> = {}
        for (const u of units) byId[u.id] = u
        set({ unitsById: byId })
      } catch {}
    },

    ensureSocket() {
      // If we have a live subscription whose socket is actually
      // connected, leave it. If the socket died (e.g., the previous
      // subscribe ran before the multi cookie was set and failed
      // auth permanently), tear down + rebuild so the new
      // subscription opens a fresh connection with the now-present
      // multi cookie.
      const existingCleanup = get().socketCleanup
      if (existingCleanup) {
        if (isMultiSocketLive()) return
        existingCleanup()
        disconnectMultiSocket()
      }
      const cleanup = subscribeMultiSocket(handlers)
      set({ socketCleanup: cleanup })
    },

    /** Probe /api/multi/session — used on screen mount to either
     *  resume into a lobby/active match or route to the hub. The
     *  socket is only opened when the probe finds a session: a
     *  cookie-less socket connection would fail auth on the server
     *  and `socketCleanup` would then short-circuit the
     *  post-create/join `ensureSocket` call, leaving the user with
     *  no live socket until reload. */
    async resume() {
      set({ phase: 'loading', error: null })
      const res = await getMultiSession()
      if (!res.ok) {
        if (res.status === 404) {
          set({ session: null, phase: 'hub', error: null })
          return
        }
        return applyError(res.code)
      }
      applySession(res.value.state)
      get().ensureSocket()
    },

    async hostLobby(body) {
      // CRITICAL: do NOT open the socket before the POST returns.
      // /api/lobby/create is what sets the multi_player_id cookie;
      // if the socket connects first, its auth handshake runs with
      // no cookie → server rejects with 'unauthenticated' → socket.io
      // -client treats middleware errors as fatal and does NOT
      // auto-reconnect. User stays without a live socket until they
      // reload the page. Open the socket AFTER applySession so the
      // cookie is in document.cookie before the first handshake.
      set({ phase: 'loading', error: null })
      const res = await createLobby(body)
      if (!res.ok) return applyError(res.code)
      applySession(res.value.state)
      get().ensureSocket()
    },

    async joinByCode(code, identity) {
      // Same cookie-before-socket discipline as hostLobby — see the
      // long comment there. /api/lobby/join sets the multi cookie;
      // opening the socket before that handshake leaves the
      // connection permanently failed.
      set({ phase: 'loading', error: null })
      const res = await joinLobby({ lobby_code: code, identity })
      if (!res.ok) return applyError(res.code)
      applySession(res.value.state)
      get().ensureSocket()
    },

    async leave() {
      const res = await leaveLobby()
      if (!res.ok) return applyError(res.code)
      const cleanup = get().socketCleanup
      if (cleanup) cleanup()
      disconnectMultiSocket()
      set({
        session: null,
        pendingBattle: null,
        pendingNextRoundState: null,
        matchOver: null,
        spectatorRounds: [],
        watchingPairingId: null,
        socketCleanup: null,
        phase: 'hub',
        buffFloaters: [],
      })
    },

    async start() {
      const res = await startMatch()
      if (!res.ok) return applyError(res.code)
      // No state in the response — match_start push will update us.
    },

    async buy(slot_index) {
      const res = await buyFromMultiShop({ slot_index })
      if (!res.ok) return applyError(res.code)
      applySession(res.value.state)
    },
    async buyToSlot(slot_index, target, opts) {
      const res = await buyFromMultiShop({ slot_index, target })
      if (!res.ok) return applyError(res.code)
      applySession(res.value.state, opts)
    },
    async sell(where, position) {
      const res = await sellFromMultiShop({ where, position })
      if (!res.ok) return applyError(res.code)
      applySession(res.value.state)
    },
    async reroll() {
      const res = await rerollMultiShop()
      if (!res.ok) return applyError(res.code)
      applySession(res.value.state)
    },
    async toggleLock(slot_index) {
      const res = await toggleMultiLock({ slot_index })
      if (!res.ok) return applyError(res.code)
      applySession(res.value.state)
    },
    async reorder(from, to) {
      const res = await reorderMultiUnits({ from, to })
      if (!res.ok) return applyError(res.code)
      applySession(res.value.state)
    },
    async combine(from, to, opts) {
      const res = await combineMultiUnits({ from, to })
      if (!res.ok) return applyError(res.code)
      applySession(res.value.state, opts)
    },
    async ready() {
      const res = await readyForMultiRound()
      if (!res.ok) return applyError(res.code)
      applySession(res.value.state)
    },
    async kick(participant_id: string) {
      const res = await kickMultiParticipant({ participant_id })
      if (!res.ok) {
        // Map structured errors to the same error envelope the rest of
        // the store uses; the participant_state_update push will
        // refresh visible chip state on success either way.
        applyError(res.code)
      }
    },
    async abandon() {
      const res = await abandonMultiSession()
      if (!res.ok && res.code !== 'no_session') {
        // Best-effort — even if the server failed we still tear down
        // local state to avoid trapping the player.
        applyError(res.code)
      }
      const cleanup = get().socketCleanup
      if (cleanup) cleanup()
      disconnectMultiSocket()
      set({
        session: null,
        pendingBattle: null,
        pendingNextRoundState: null,
        matchOver: null,
        spectatorRounds: [],
        watchingPairingId: null,
        socketCleanup: null,
        phase: 'hub',
        error: null,
        buffFloaters: [],
      })
    },

    dismissBattle() {
      // If the next round's state already arrived (server's intermission
      // expired before the player closed their replay), apply it
      // immediately so they don't sit on a blank "between" screen.
      const cur = get()
      const next = cur.pendingNextRoundState
      if (next) {
        applySession(next)
        set({ pendingBattle: null, pendingNextRoundState: null })
        return
      }
      // If the local player got eliminated by this round's battle,
      // route them to the spectator picker instead of the inter-
      // round shop splash — there's no next shop for them. The
      // is_eliminated flag was set by the participant_state_update
      // that arrived just after round_battle_resolved; the in-
      // progress 'battle' phase guard in applySession held the
      // BattleScreen until the player closed it themselves.
      const eliminated = cur.session?.is_eliminated ?? false
      set({
        pendingBattle: null,
        phase: eliminated ? 'spectator' : 'between',
      })
    },
    watchSpectatorPairing(pairing_id) {
      set({ watchingPairingId: pairing_id, phase: 'battle' })
    },
    closeSpectatorReplay() {
      set({ watchingPairingId: null, phase: 'spectator' })
    },
    clearNotice() {
      set({ notice: null })
    },
    exitMatch() {
      const cleanup = get().socketCleanup
      if (cleanup) cleanup()
      disconnectMultiSocket()
      set({
        session: null,
        pendingBattle: null,
        pendingNextRoundState: null,
        matchOver: null,
        spectatorRounds: [],
        watchingPairingId: null,
        socketCleanup: null,
        phase: 'idle',
        buffFloaters: [],
      })
    },
  }
})

export type MultiPhase = Phase
export type { RoundTimerSeconds }
